// „Mój wygląd”: podgląd awatara, imię (z ołówkiem, gdy dziecko może je zmienić) i trzy grupy awatarów — Dinozaury, Pokémon, Neutralne.
// Wybór awatara ustawia też motyw (grupa = motyw). Używany w trzech miejscach: dziecko zmienia swój wygląd, rodzic zakłada profil (pierwszy ekran
// i panel rodzica) i rodzic edytuje profil. Każda zmiana od razu wywołuje `onChange` z całym wyglądem.
import type { Pair } from '../lib/i18n.ts';
import type { Look } from '../lib/look.ts';
import { PROFILE_NAME_MAX } from '../lib/look.ts';
import { AVATAR_SETS, avatarEl, avatarName, resolveAvatar, setTitle } from './avatars.ts';
import type { AppCtx } from './ctx.ts';
import { h } from './dom.ts';
import { tx, txAttr } from './i18n.ts';
import { liveLang } from './i18nLive.ts';
import { icon } from './kit.ts';

export interface LookPicker {
  el: HTMLElement;
  get(): Look;
}

const T = {
  rename: { en: 'Change name', pl: 'Zmień imię' },
  nameField: { en: 'Name', pl: 'Imię' },
  saveName: { en: 'Save name', pl: 'Zapisz imię' },
} as const satisfies Record<string, Pair>;

export interface LookPickerOpts {
  /** Ekran dziecka (albo pierwszy profil) podaje `ctx`: napisy idą wtedy za przełącznikiem EN/PL. Bez niego (panel rodzica) są po polsku. */
  ctx?: Pick<AppCtx, 'teacher' | 'account'>;
  /** Imię do pokazania przy podglądzie (bez możliwości zmiany tutaj). */
  name?: () => string;
  /** Gdy podane, imię ma ołówek: dziecko je zmienia. Zwraca '' po powodzeniu albo komunikat o błędzie (imię zajęte itp.). */
  rename?: { current: () => string; save: (name: string) => Promise<string> };
}

/** Znaczek grupy jak na makiecie: dinozaur (emoji), Poké Ball i lawendowa kostka rysowane w CSS (nie ma na to emoji). */
function groupIcon(id: string, glyph: string): HTMLElement {
  if (id === 'pokemon') return h('span', { class: 'lp-group-icon ball', 'aria-hidden': 'true' });
  if (id === 'neutral') return h('span', { class: 'lp-group-icon cube', 'aria-hidden': 'true' });
  return h('span', { class: 'lp-group-icon', 'aria-hidden': 'true' }, glyph);
}

export function createLookPicker(initial: Look, onChange: (look: Look) => void = () => {}, opts: LookPickerOpts = {}): LookPicker {
  let look: Look = { ...initial };
  const root = h('div', { class: 'look-picker', 'data-testid': 'look-picker' });
  let editing = false;
  let nameNote = '';
  let saving = false;
  let draft: string | null = null; // wpisywane imię przeżywa przerysowanie (np. gdy dziecko w trakcie kliknie awatar)
  const ctx = opts.ctx;
  const lang = (): 'en' | 'pl' => (ctx?.teacher ? ctx.teacher.lang() : 'pl');
  /** Napis układany przy rysowaniu (całość `paint()` rysuje się od nowa po zmianie języka). */
  const now = (p: Pair): string => p[lang()];
  /** Element rysowany raz (siatka awatarów): napisy przepisują się same. */
  const attr = <E extends Element>(el: E, name: string, p: Pair): E => (ctx ? txAttr(ctx, el, name, p) : (el.setAttribute(name, p.pl), el));

  const change = (patch: Partial<Look>): void => {
    look = { ...look, ...patch };
    paint();
    onChange({ ...look });
  };

  function nameBox(): HTMLElement | null {
    const r = opts.rename;
    if (!r) {
      const name = opts.name?.() ?? '';
      return name ? h('div', { class: 'lp-name static' }, h('strong', { 'data-testid': 'lp-name' }, name)) : null;
    }
    if (!editing) {
      return h('button', { type: 'button', class: 'lp-name', 'data-testid': 'name-edit', 'aria-label': now(T.rename), title: now(T.rename), onclick: () => { editing = true; draft = null; nameNote = ''; paint(); } },
        h('strong', { 'data-testid': 'lp-name' }, r.current()), icon('edit'));
    }
    const input = h('input', { type: 'text', class: 'lp-name-input', maxlength: PROFILE_NAME_MAX, autocomplete: 'off', value: draft ?? r.current(), 'aria-label': now(T.nameField), 'data-testid': 'name-input' });
    input.addEventListener('input', () => {
      draft = input.value;
    });
    const save = async (): Promise<void> => {
      if (saving) return;
      saving = true;
      const msg = await r.save(input.value);
      saving = false;
      nameNote = msg;
      if (!msg) {
        editing = false;
        draft = null;
      }
      paint();
    };
    input.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') void save();
    });
    queueMicrotask(() => input.focus());
    return h('div', { class: 'lp-name editing' }, input, h('button', { type: 'button', class: 'lp-name-ok', 'data-testid': 'name-save', 'aria-label': now(T.saveName), title: now(T.saveName), onclick: () => void save() }, icon('check'), h('span', null, 'OK')));
  }

  // Siatkę awatarów budujemy raz: przy zmianie wyboru przełączamy tylko zaznaczenie, żeby obrazki nie migały (i żeby nie gubić wpisywanego imienia).
  const avatarButtons: { el: HTMLElement; id: string }[] = [];
  const groups = h('div', { class: 'lp-groups' },
    ...AVATAR_SETS.map((set) =>
      h('section', { class: `lp-group g-${set.id}`, 'data-testid': 'avatar-group', 'data-set': set.id },
        h('h3', { class: 'lp-group-title' }, groupIcon(set.id, set.icon), ctx ? tx(ctx, setTitle(set)) : set.title),
        attr(
          h('div', { class: 'lp-grid', role: 'group', 'data-testid': 'avatar-grid' },
            ...set.avatars.map((a) => {
              const id = `${set.id}:${a.name}`;
              const el = h('button', {
                type: 'button',
                class: 'lp-avatar',
                'aria-pressed': 'false',
                'data-testid': 'avatar-opt',
                'data-avatar-id': id,
                onclick: () => change({ avatar: id, theme: set.id }),
              }, avatarEl({ avatar: id, theme: set.id }, 'md', ctx));
              attr(el, 'aria-label', avatarName(a));
              attr(el, 'title', avatarName(a));
              avatarButtons.push({ el, id });
              return el;
            }),
          ),
          'aria-label',
          setTitle(set),
        ),
      ),
    ),
  );

  /** Duży podgląd; jego opis dla czytnika ekranu układamy w bieżącym języku (całość rysuje się od nowa po zmianie EN/PL). */
  function previewAvatar(chosen: ReturnType<typeof resolveAvatar>): HTMLElement {
    const el = avatarEl(look, 'lg');
    el.setAttribute('aria-label', now(avatarName(chosen.def)));
    return el;
  }

  function paint(): void {
    const chosen = resolveAvatar(look);
    for (const b of avatarButtons) {
      const on = chosen.id === b.id;
      b.el.className = `lp-avatar${on ? ' on' : ''}`;
      b.el.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    root.replaceChildren(
      h('div', { class: 'lp-hero' },
        h('div', { class: 'lp-preview' }, previewAvatar(chosen), h('span', { class: 'lp-spark', 'aria-hidden': 'true' })),
        h('div', { class: 'lp-who' }, nameBox(), nameNote && h('p', { class: 'lp-note', role: 'alert', 'data-testid': 'name-note' }, nameNote), h('span', { class: 'muted', 'data-testid': 'avatar-label' }, now(avatarName(chosen.def)))),
      ),
      groups,
    );
  }
  paint();
  if (ctx) liveLang(ctx, root, paint); // nazwa wybranego awatara, ołówek i pole imienia układają się na nowo (wpisywane imię zostaje w `draft`)
  return { el: root, get: () => ({ ...look }) };
}
